import React, { useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { masteryDirectionEnums } from 'utils';
import { buildBucketTooltipHtml, buildEventBuckets, EVENT_TOOLTIP_FONT_FAMILY, formatInsightDate } from './chartEventHelpers';
import { formatChartValueByDataType, getChartValueLabelByDataType, isPercentageDataType } from './dataTypeChart.helpers';
import { buildAverageLine, buildLinearTrendLine } from './trendLine.helpers';
import { buildBaselinePhaseSeries, buildPhaseChangeMarkLine, getBaselinePhaseLayout } from './baselinePhase.helpers';

const FONT_FAMILY = '"GT Walsheim Pro", "Open Sans", sans-serif';

const getTimeAxisLabel = (categories = []) => {
    const sample = categories.find(Boolean);
    if (!sample) return 'Time';
    if (sample.includes('-W')) return 'Time';
    if (sample.length > 7) return 'Time';
    return 'Time';
};

const getMasteryDirectionLabel = (direction) => {
    switch (direction) {
        case masteryDirectionEnums.ABOVE:
            return '↑';
        case masteryDirectionEnums.BELOW:
            return '↓';
        case masteryDirectionEnums.EQUAL:
            return '=';
        default:
            return '';
    }
};

// Per-session (per data point) mastery check — distinct from the target's
// `status` (which only flips to MASTERED once, per the org's consecutive-
// session criteria). This answers "did THIS session's value cross the
// mastery threshold", independent of whether the target was ever marked
// mastered.
export const passesMasteryThreshold = (value, masteryValue, direction) => {
    if (value === null || value === undefined || masteryValue === null || masteryValue === undefined) return null;
    const numericValue = Number(value);
    const numericThreshold = Number(masteryValue);
    switch (direction) {
        case masteryDirectionEnums.BELOW:
            return numericValue <= numericThreshold;
        case masteryDirectionEnums.EQUAL:
            return numericValue === numericThreshold;
        case masteryDirectionEnums.ABOVE:
        default:
            return numericValue >= numericThreshold;
    }
};

// `baselinePhase`: the target's ACTIVE baseline, [{ date, value }] one per session — drawn as its own phase left of
// the range (shown while the Baseline legend item is on).
export const TargetProgressChart = ({ chartRef, data, baselinePhase = [], dataType, mastery, events = [], animate = true }) => {
    const [showProgressLine, setShowProgressLine] = useState(true);
    const [showBaselineLine, setShowBaselineLine] = useState(true);
    const [showBaselineTrendLine, setShowBaselineTrendLine] = useState(false);
    const [showMasteryLine, setShowMasteryLine] = useState(true);
    const [showTrendLine, setShowTrendLine] = useState(true);
    const [showAverageLine, setShowAverageLine] = useState(true);
    const [showClientEvents, setShowClientEvents] = useState(true);
    const [showGoalEvents, setShowGoalEvents] = useState(true);
    const [showTargetEvents, setShowTargetEvents] = useState(true);
    const hasMasteryLine = mastery?.count !== null && mastery?.count !== undefined;
    const masteryDisplayValue = hasMasteryLine ? formatChartValueByDataType(mastery?.count, dataType) : '';
    const hasSessionData = Array.isArray(data) && data.some((point) => point?.value !== null && point?.value !== undefined);
    const hasBaselineData =
        Array.isArray(baselinePhase) && baselinePhase.some((point) => point?.value !== null && point?.value !== undefined);
    const hasRenderableData = hasSessionData || hasBaselineData;
    const masteryStats = useMemo(() => {
        if (!hasMasteryLine || !Array.isArray(data)) return null;
        let passed = 0;
        let total = 0;
        data.forEach((point) => {
            if (point?.value === null || point?.value === undefined) return;
            total += 1;
            if (passesMasteryThreshold(point.value, mastery?.count, mastery?.direction)) passed += 1;
        });
        return total > 0 ? { passed, total } : null;
    }, [data, hasMasteryLine, mastery]);
    const option = useMemo(() => {
        if (!data?.length) return null;

        const rangeCategories = data.map((p) => p.date);
        const phaseLayout = getBaselinePhaseLayout(showBaselineLine ? baselinePhase : [], rangeCategories.length);
        const categories = [...phaseLayout.leading, ...rangeCategories];
        const timeAxisLabel = getTimeAxisLabel(rangeCategories);
        const rangeSeriesData = data.map((p) => p.value);
        const seriesData = phaseLayout.pad(rangeSeriesData);
        const numericSeriesData = rangeSeriesData.filter((value) => value !== null && value !== undefined);
        const numericBaselineData = phaseLayout.phase.map((point) => point.value);
        const filteredEvents = events.filter((event) => {
            if (event.type === 'Client') return showClientEvents;
            if (event.type === 'Goal') return showGoalEvents;
            if (event.type === 'Target') return showTargetEvents;
            return false;
        });
        const eventBuckets = buildEventBuckets(filteredEvents, rangeCategories);
        const eventBucketMap = new Map(eventBuckets.map((bucket) => [bucket.key, bucket]));
        const yAxisMax = Math.max(...numericSeriesData, 0);
        const isPercentType = isPercentageDataType(dataType);
        const masteryValue = hasMasteryLine ? mastery?.count : null;
        const resolvedMax = Math.max(
            yAxisMax,
            ...numericBaselineData,
            masteryValue !== null && masteryValue !== undefined ? Number(masteryValue) : 0
        );
        const yAxisDisplayMax = isPercentType ? 100 : Math.max(Math.ceil(resolvedMax * 1.12), resolvedMax + 1, 1);
        const eventMarkerY = Math.max(resolvedMax, yAxisDisplayMax * 0.9);
        const valueLabel = getChartValueLabelByDataType(dataType);
        const trendLine = showTrendLine
            ? buildLinearTrendLine({
                  values: rangeSeriesData,
                  maxValue: yAxisDisplayMax,
                  decimals: 2,
              })
            : { seriesData: [] };
        const averageLine = showAverageLine
            ? buildAverageLine({
                  values: rangeSeriesData,
                  decimals: 2,
              })
            : { value: null };
        const baselineTrendLine = showBaselineTrendLine
            ? buildLinearTrendLine({
                  values: phaseLayout.values,
                  maxValue: yAxisDisplayMax,
                  decimals: 2,
              })
            : { seriesData: [] };
        const markLineData = eventBuckets.map((bucket) => ({
            xAxis: bucket.key,
            lineStyle: {
                color: bucket.color,
                type: 'dashed',
                width: 1.5,
            },
            label: { show: false },
        }));
        if (phaseLayout.leading.length) markLineData.push(buildPhaseChangeMarkLine());
        const eventMarkerSeries = eventBuckets.some((bucket) => bucket.count > 1)
            ? [
                  {
                      name: 'Event Counts',
                      type: 'scatter',
                      silent: true,
                      data: eventBuckets
                          .filter((bucket) => bucket.count > 1)
                          .map((bucket) => ({
                              value: [bucket.key, eventMarkerY],
                              count: bucket.count,
                              itemStyle: { color: bucket.color },
                              label: {
                                  show: true,
                                  formatter: `${bucket.count}`,
                                  color: '#FFFFFF',
                                  fontSize: 10,
                                  fontWeight: 700,
                                  fontFamily: FONT_FAMILY,
                              },
                          })),
                      symbol: 'circle',
                      symbolSize: 18,
                      z: 6,
                  },
              ]
            : [];
        const eventHoverSeries =
            eventBuckets.length > 0
                ? [
                      {
                          name: 'Event Hover',
                          type: 'bar',
                          data: categories.map((category) => (eventBucketMap.has(category) ? yAxisDisplayMax : 0)),
                          barWidth: 12,
                          barGap: '-100%',
                          itemStyle: {
                              color: 'rgba(0, 0, 0, 0)',
                          },
                          emphasis: {
                              itemStyle: {
                                  color: 'rgba(0, 0, 0, 0)',
                              },
                          },
                          tooltip: {
                              show: false,
                          },
                          z: 1,
                      },
                  ]
                : [];

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'line' },
                extraCssText: `box-shadow:none;background:transparent;border:none;padding:0;font-family:${EVENT_TOOLTIP_FONT_FAMILY};`,
                textStyle: { fontFamily: FONT_FAMILY, fontSize: 12 },
                formatter: (params) => {
                    if (!params?.length) return '';
                    const axisValue = params[0].axisValue;
                    if (phaseLayout.formatKey(axisValue) === '') return '';
                    const isPhasePoint = phaseLayout.formatKey(axisValue) !== null;
                    const linePoint = params.find((param) => param.seriesType === 'line');
                    const baselinePoint = params.find((param) => param.seriesName === 'Baseline');
                    const label = isPhasePoint
                        ? `Baseline · ${phaseLayout.formatKey(axisValue, true)}`
                        : formatInsightDate(axisValue, 'MMM YYYY', 'DD MMM YYYY');
                    const eventTooltip = buildBucketTooltipHtml(eventBucketMap.get(axisValue));
                    const displayValue = formatChartValueByDataType(linePoint?.value, dataType);
                    const baselineHtml =
                        baselinePoint && baselinePoint?.value !== null && baselinePoint?.value !== undefined
                            ? `
                            <div style="display:flex; align-items:center; gap:6px; margin-top:4px;">
                                <p style="color:#101828; margin:0;">▲</p>
                                <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                    Baseline:
                                    <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${formatChartValueByDataType(baselinePoint.value, dataType)}</span>
                                </p>
                            </div>
                        `
                            : '';
                    const summaryCard = `
                        <div style="padding:12px 8px; background:white; border-radius:8px; box-shadow:0 0 6px 0 rgba(138, 138, 138, 0.24); margin-bottom:${eventTooltip ? '8px' : '0'}; font-family:${EVENT_TOOLTIP_FONT_FAMILY};">
                            <p style="color:#0C66E4; font-size:12px; font-weight:600; line-height:18px; margin:0 0 4px;">${label}</p>
                            ${
                                isPhasePoint
                                    ? ''
                                    : `<div style="display:flex; align-items:center; gap:6px;">
                                <p style="color:#0C66E4; margin:0;">●</p>
                                <p style="color:#344054; font-size:12px; font-weight:400; line-height:18px; margin:0;">
                                    ${valueLabel}:
                                    <span style="color:#344054; font-size:12px; font-weight:600; line-height:18px;"> ${displayValue}</span>
                                </p>
                            </div>`
                            }
                            ${baselineHtml}
                        </div>
                    `;

                    return `${summaryCard}${eventTooltip}`;
                },
            },
            grid: { left: '7%', right: '4%', top: '8%', bottom: '18%', containLabel: true },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: categories,
                name: timeAxisLabel,
                nameLocation: 'middle',
                nameGap: 30,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { lineStyle: { color: '#E5E7EB' } },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: (v) => phaseLayout.formatKey(v) ?? formatInsightDate(v),
                },
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: yAxisDisplayMax,
                name: valueLabel,
                nameLocation: 'middle',
                nameGap: 52,
                nameRotate: 90,
                nameTextStyle: {
                    color: '#667085',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                },
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: '#98A2B3',
                    fontSize: 11,
                    fontFamily: FONT_FAMILY,
                    formatter: (value) => formatChartValueByDataType(value, dataType),
                },
                splitLine: { lineStyle: { color: '#F2F4F7' } },
            },
            series: [
                ...(showProgressLine
                    ? [
                          {
                              name: 'Value',
                              type: 'line',
                              data: seriesData.map((value) => {
                                  if (value === null || value === undefined) return value;
                                  const passed = hasMasteryLine
                                      ? passesMasteryThreshold(value, masteryValue, mastery?.direction)
                                      : null;
                                  if (passed === null) return value;
                                  // Per-session mastery-threshold pass/fail: green = this
                                  // session's value crossed mastery, white = it didn't —
                                  // independent of the target's overall status.
                                  return {
                                      value,
                                      itemStyle: {
                                          color: passed ? '#12B76A' : '#FFFFFF',
                                          borderColor: passed ? '#12B76A' : '#0C66E4',
                                          borderWidth: 2,
                                      },
                                  };
                              }),
                              connectNulls: true,
                              symbol: 'circle',
                              showSymbol: true,
                              // Every session point, even on a dense (daily) axis — ECharts' 'auto' hides some.
                              showAllSymbol: true,
                              symbolSize: (value) => (value === null || value === undefined ? 0 : 6),
                              lineStyle: { color: '#0C66E4', width: 2 },
                              itemStyle: { color: '#FFFFFF', borderColor: '#0C66E4', borderWidth: 2 },
                              markLine:
                                  markLineData.length > 0
                                      ? {
                                            symbol: 'none',
                                            data: markLineData,
                                        }
                                      : undefined,
                          },
                      ]
                    : []),
                ...buildBaselinePhaseSeries(phaseLayout, rangeCategories.length),
                ...(showBaselineTrendLine && baselineTrendLine.seriesData.length > 0
                    ? [
                          {
                              name: 'Baseline Trend',
                              type: 'line',
                              // Over the baseline phase only.
                              data: [...baselineTrendLine.seriesData, null, ...rangeCategories.map(() => null)],
                              smooth: false,
                              symbol: 'circle',
                              showSymbol: false,
                              silent: true,
                              lineStyle: {
                                  color: '#667085',
                                  width: 2,
                                  opacity: 0.95,
                              },
                              z: 2,
                          },
                      ]
                    : []),
                ...(showMasteryLine && masteryValue !== null && masteryValue !== undefined
                    ? [
                          {
                              name: 'Mastery Threshold',
                              type: 'line',
                              data: [],
                              symbol: 'none',
                              silent: true,
                              markLine: {
                                  symbol: 'none',
                                  silent: true,
                                  data: [
                                      {
                                          yAxis: Number(masteryValue),
                                          label: {
                                              show: true,
                                              formatter: `Mastery (${getMasteryDirectionLabel(mastery?.direction)} ${masteryDisplayValue})`,
                                              position: 'insideEndTop',
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: '#12B76A',
                                              fontFamily: FONT_FAMILY,
                                          },
                                          lineStyle: {
                                              color: '#12B76A',
                                              type: 'dashed',
                                              width: 1.5,
                                          },
                                      },
                                  ],
                              },
                              z: 2,
                          },
                      ]
                    : []),
                ...(showAverageLine && averageLine.value !== null
                    ? [
                          {
                              name: 'Average',
                              type: 'line',
                              data: [],
                              symbol: 'none',
                              silent: true,
                              markLine: {
                                  symbol: 'none',
                                  silent: true,
                                  data: [
                                      {
                                          yAxis: averageLine.value,
                                          label: {
                                              show: true,
                                              formatter: `Avg (${formatChartValueByDataType(averageLine.value, dataType)})`,
                                              position: 'insideEndBottom',
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: '#7A5AF8',
                                              fontFamily: FONT_FAMILY,
                                          },
                                          lineStyle: {
                                              color: '#7A5AF8',
                                              type: 'dashed',
                                              width: 1.5,
                                          },
                                      },
                                  ],
                              },
                              z: 2,
                          },
                      ]
                    : []),
                ...(showTrendLine && trendLine.seriesData.length > 0
                    ? [
                          {
                              name: 'Trend',
                              type: 'line',
                              data: phaseLayout.pad(trendLine.seriesData),
                              smooth: false,
                              symbol: 'none',
                              silent: true,
                              lineStyle: {
                                  color: '#FF6B6B',
                                  width: 2,
                                  opacity: 0.95,
                              },
                              z: 3,
                          },
                      ]
                    : []),
                ...eventHoverSeries,
                ...eventMarkerSeries,
            ],
        };
    }, [
        baselinePhase,
        data,
        dataType,
        events,
        hasMasteryLine,
        mastery,
        showAverageLine,
        showBaselineLine,
        showBaselineTrendLine,
        showClientEvents,
        showGoalEvents,
        showMasteryLine,
        showProgressLine,
        showTargetEvents,
        showTrendLine,
    ]);

    if (!option) return null;

    return (
        <>
            <div className="target-progress-chart-shell">
                <ReactECharts
                    ref={chartRef}
                    option={animate ? option : { ...option, animation: false }}
                    notMerge={true}
                    className="insights-chart-target"
                    opts={{ renderer: 'canvas' }}
                />
                {!hasRenderableData && (
                    <div className="target-progress-empty-state">
                        <p className="target-progress-empty-title">No session data yet</p>
                        <p className="target-progress-empty-text">No data has been recorded for this target in the selected date range.</p>
                    </div>
                )}
            </div>
            <div className="insights-chart-legend insights-chart-legend-small">
                <button
                    className={`insights-chart-legend-item ${showProgressLine ? 'active' : ''}`}
                    onClick={() => setShowProgressLine((prev) => !prev)}
                >
                    <span className="insights-chart-legend-line insights-chart-legend-line-primary" />
                    <span>Progress</span>
                </button>
                {hasMasteryLine && (
                    <button
                        className={`insights-chart-legend-item ${showMasteryLine ? 'active' : ''}`}
                        onClick={() => setShowMasteryLine((prev) => !prev)}
                    >
                        <span className="insights-chart-legend-dash insights-chart-legend-dash-target-mastery" />
                        <span>Mastery</span>
                    </button>
                )}
                {masteryStats && (
                    <span className="insights-chart-legend-static" title="Sessions whose value crossed the mastery threshold">
                        <span className="donut-legend-dot" style={{ background: '#12B76A' }} />
                        <span>
                            {masteryStats.passed}/{masteryStats.total} sessions met mastery
                        </span>
                    </span>
                )}
                <button
                    className={`insights-chart-legend-item ${showBaselineLine ? 'active' : ''}`}
                    onClick={() => setShowBaselineLine((prev) => !prev)}
                >
                    <span className="insights-chart-legend-line insights-chart-legend-line-baseline insights-chart-legend-line-triangle" />
                    <span>Baseline</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showBaselineTrendLine ? 'active' : ''}`}
                    onClick={() => setShowBaselineTrendLine((prev) => !prev)}
                >
                    <span className="insights-chart-legend-line insights-chart-legend-line-baseline-trend insights-chart-legend-line-plain" />
                    <span>Baseline Trend</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showTrendLine ? 'active' : ''}`}
                    onClick={() => setShowTrendLine((prev) => !prev)}
                >
                    <span className="insights-chart-legend-line insights-chart-legend-line-trend insights-chart-legend-line-plain" />
                    <span>Trend</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showAverageLine ? 'active' : ''}`}
                    onClick={() => setShowAverageLine((prev) => !prev)}
                >
                    <span className="insights-chart-legend-dash insights-chart-legend-dash-average" />
                    <span>Average</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showClientEvents ? 'active' : ''}`}
                    onClick={() => setShowClientEvents((prev) => !prev)}
                >
                    <span className="insights-chart-legend-vertical insights-chart-legend-vertical-client" />
                    <span>Client Events</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showGoalEvents ? 'active' : ''}`}
                    onClick={() => setShowGoalEvents((prev) => !prev)}
                >
                    <span className="insights-chart-legend-vertical insights-chart-legend-vertical-goal" />
                    <span>Goal Events</span>
                </button>
                <button
                    className={`insights-chart-legend-item ${showTargetEvents ? 'active' : ''}`}
                    onClick={() => setShowTargetEvents((prev) => !prev)}
                >
                    <span className="insights-chart-legend-vertical insights-chart-legend-vertical-target" />
                    <span>Target Events</span>
                </button>
            </div>
        </>
    );
};
